import {AuthService} from "./AuthService";
import {isAdmin, isManager, userId} from "../utils/Utils";

export class AccountService {
    url_account = `accounts/`;
    url_billing_address = `billing_addresses/`;
    url_payment_method = `payment_methods/`;

    updateMyAccount(user){
        return AuthService.apiPut(this.url_account + userId() + "/", {user}).then(res => res.data.data);
    }

    changeMyPassword(old_password, new_password){
        return AuthService.apiPut(this.url_account + userId() + "/change_password/", {old_password, new_password}).then(res => res.data.data);
    }

    saveGuidingCompleted(guiding_page){
        return AuthService.apiPut(this.url_account + userId() + "/complete_guiding/", {completed_guiding_page: guiding_page}).then(res => res.data.data);
    }


    calculateDebitForPlan(planId, added_additional_feature_names=''){
        return AuthService.apiGet(this.url_account + userId() + "/debit/",{params: {pricing_plan_id: planId, added_additional_feature_names: added_additional_feature_names}}).then(res => res.data.data);
    }

    changeMyPlan(pricing_plan_id, added_additional_feature_names = ''){
        let params = {
            pricing_plan_id: pricing_plan_id,
            added_additional_feature_names: added_additional_feature_names
        };

        return AuthService.apiPost(this.url_account + userId() + "/subscribe/", params).then(res => res.data.data);
    }

    // The caller's current active subscription, fetched fresh from the backend
    // (so a plan change synced via webhook is reflected without re-login).
    getMySubscription(){
        return AuthService.apiGet(this.url_account + "current_subscription/").then(res => res.data.data);
    }

    // The current and the next period, fetched fresh so the renewal reminder does not
    // work from what was stored at login.
    refreshSubscriptions(){
        return AuthService.apiGet(this.url_account + "current_subscription/").then(res => {
            localStorage.setItem("subscription", JSON.stringify(res.data.data));
            if (res.data.next_subscription)
                localStorage.setItem("next_subscription", JSON.stringify(res.data.next_subscription));
            else
                localStorage.removeItem("next_subscription");
        });
    }

    // Bank-transfer customers: "I've made the payment" / "Don't show again this month".
    reportPayment(){
        return AuthService.apiPost(this.url_account + "payment_reminder/report_payment/", {}).then(res => res.data.data);
    }

    dismissPaymentReminder(){
        return AuthService.apiPost(this.url_account + "payment_reminder/dismiss/", {}).then(res => res.data.data);
    }

    // Shopify-app merchants: create a Shopify subscription for the chosen plan
    // and get the approval URL to redirect to.
    getShopifySubscriptionUrl(pricing_plan_id, return_url){
        return AuthService.apiPost(this.url_account + "shopify_subscription_url/", {
            plan_id: pricing_plan_id,
            return_url: return_url,
        }).then(res => res.data);
    }

    // Shopify-app merchants: confirm a just-approved subscription by its charge
    // id (from the billing return URL) and sync the plan immediately.
    confirmShopifySubscription(charge_id){
        return AuthService.apiPost(this.url_account + "confirm_shopify_subscription/", {
            charge_id: charge_id,
        }).then(res => res.data);
    }

    cancelMyPlan(){
        return AuthService.apiPut(this.url_account + userId() + "/cancel_subscription/").then(res => res.data.data);
    }

    renewMyPlan(){
        return AuthService.apiPost(this.url_account + userId() + "/renew_subscription/").then(res => res.data.data);
    }

    tryChargeAgain(user_id){
        return AuthService.apiPost(this.url_account + user_id + "/try_charge_again/").then(res => res.data.data);
    }

    // Server-side calculation of the charge breakdown (VAT, exchange rate,
    // final amount) shown to the admin before confirming the charge.
    previewCharge(user_id, amount, vat_rate, exchange_rate){
        let params = {amount: amount, vat_rate: vat_rate, exchange_rate: exchange_rate};
        return AuthService.apiPost(this.url_account + user_id + "/charge_preview/", params).then(res => res.data.data);
    }

    chargeUser(user_id, amount, vat_rate, exchange_rate){
        let params = {amount: amount, vat_rate: vat_rate, exchange_rate: exchange_rate};
        return AuthService.apiPost(this.url_account + user_id + "/charge/", params).then(res => res.data.data);
    }

    // Admin: start a trial of `days` days on an account with no active subscription.
    startTrial(user_id, days){
        return AuthService.apiPost(this.url_account + user_id + "/start_trial/", {days: days}).then(res => res.data.data);
    }

    getBillingAddress(){
        return AuthService.apiGet(this.url_billing_address,{params: {user_id: userId()}}).then(res => res.data.data);
    }

    getPaymentMethod(){
        return AuthService.apiGet(this.url_payment_method,{params: {user_id: userId()}}).then(res => res.data.data);
    }

    saveBillingAddress(billing_address){
        if(billing_address.id) {
            return AuthService.apiPut(this.url_billing_address + billing_address.id + "/", billing_address).then(res => res.data.data);
        } else {
            return AuthService.apiPost(this.url_billing_address, billing_address).then(res => res.data.data);
        }
    }

    savePaymentMethod(payment_method){
        if(payment_method.id) {
            return AuthService.apiPut(this.url_payment_method + payment_method.id + "/", payment_method).then(res => res.data.data);
        } else {
            return AuthService.apiPost(this.url_payment_method, payment_method).then(res => res.data.data);
        }
    }

    impersonate(user_id){
        return AuthService.apiPut(this.url_account + user_id + "/impersonate/").then(res => {
            if(isAdmin()) {
                res.data.user.is_admin = true
            } else if (isManager()) {
                res.data.user.is_manager = true
            }
            AuthService.setUserAndToken(res);
            return res;
        });
    }

    sendMessage(user_id, message){
        let params = {message: message};
        return AuthService.apiPost(this.url_account + user_id +  "/send_message/", params).then(res => res.data.data);
    }

    listMessages(user_id){
        return AuthService.apiGet(this.url_account + user_id + "/messages/").then(res => res.data.data);
    }

    listAllMessages(){
        return AuthService.apiGet(this.url_account + "all_messages/").then(res => res.data.data);
    }

    setAllMessagesRead(){
        return AuthService.apiPost(this.url_account + userId() + "/read_messages/").then(res => res.data.data);
    }

}
